import { useEffect, useId, useState, type FormEvent } from "react";

import type { CategoryRef, WatchItem } from "../lib/api";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useRenameCategory,
  useUpdateWatch,
} from "../lib/queries";
import { Dialog } from "./Dialog";
import { useToast } from "./Toast";

export function CategoryChips({ categories }: { categories: CategoryRef[] }) {
  if (!categories.length) return null;
  return (
    <ul className="chips" aria-label="Catégories">
      {categories.map((c) => (
        <li key={c.id} className="chip">
          {c.name}
        </li>
      ))}
    </ul>
  );
}

interface PickerProps {
  selected: number[];
  onChange: (ids: number[]) => void;
  legend?: string;
  allowCreate?: boolean;
}

/** Toggle existing categories on or off, and create new ones on the spot. */
export function CategoryPicker({ selected, onChange, legend = "Catégories", allowCreate = true }: PickerProps) {
  const categories = useCategories();
  const create = useCreateCategory();
  const [name, setName] = useState("");
  const inputId = useId();

  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  function add() {
    const clean = name.trim();
    if (!clean) return;
    const existing = categories.data?.find((c) => c.name.toLowerCase() === clean.toLowerCase());
    if (existing) {
      if (!selected.includes(existing.id)) onChange([...selected, existing.id]);
      setName("");
      return;
    }
    create.mutate(clean, {
      onSuccess: (category) => {
        onChange([...selected, category.id]);
        setName("");
      },
    });
  }

  return (
    <fieldset className="category-picker">
      <legend className="field__label">{legend}</legend>
      {categories.data && categories.data.length > 0 ? (
        <div className="chips chips--toggle">
          {categories.data.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chip"
              aria-pressed={selected.includes(c.id)}
              onClick={() => toggle(c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : (
        categories.isSuccess && <p className="field__hint">Aucune catégorie pour l'instant.</p>
      )}
      {allowCreate && (
        <div className="category-picker__new">
          <label className="visually-hidden" htmlFor={inputId}>
            Nouvelle catégorie
          </label>
          <input
            id={inputId}
            className="input"
            maxLength={80}
            placeholder="Nouvelle catégorie, ex. : Clients"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              // Inside a larger form: Enter creates the category instead of submitting.
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
          />
          <button
            type="button"
            className="button button--quiet button--small"
            onClick={add}
            disabled={!name.trim() || create.isPending}
          >
            Créer et cocher
          </button>
        </div>
      )}
      {create.isError && (
        <span className="field__error" role="alert">
          {create.error.message}
        </span>
      )}
    </fieldset>
  );
}

export function WatchCategoriesDialog({
  watch,
  open,
  onClose,
}: {
  watch: Pick<WatchItem, "id" | "siren" | "categories">;
  open: boolean;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const update = useUpdateWatch(watch.siren);
  const toast = useToast();

  useEffect(() => {
    if (open) setSelected(watch.categories.map((c) => c.id));
  }, [open, watch.categories]);

  function save(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      { id: watch.id, category_ids: selected },
      {
        onSuccess: () => {
          toast("Catégories enregistrées.");
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title="Catégories de l'entreprise">
      <form onSubmit={save}>
        <CategoryPicker selected={selected} onChange={setSelected} legend="Cochez les catégories" />
        {update.isError && (
          <div className="notice notice--error" role="alert">
            {update.error.message}
          </div>
        )}
        <div className="actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="button" disabled={update.isPending}>
            Enregistrer les catégories
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ManageCategoriesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const categories = useCategories();
  const create = useCreateCategory();
  const [name, setName] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(name.trim(), { onSuccess: () => setName("") });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Gérer les catégories" wide>
      <p>Les catégories servent à ranger et filtrer vos entreprises. Elles ne sont visibles que par vous.</p>
      <ul className="category-list">
        {categories.data?.map((c) => (
          <CategoryLine key={c.id} id={c.id} name={c.name} count={c.watch_count} />
        ))}
        {categories.data?.length === 0 && <li className="field__hint">Aucune catégorie pour l'instant.</li>}
      </ul>
      <form className="category-picker__new" onSubmit={submit}>
        <label className="visually-hidden" htmlFor="new-category">
          Nouvelle catégorie
        </label>
        <input
          id="new-category"
          className="input"
          maxLength={80}
          placeholder="Nouvelle catégorie"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="button button--small" disabled={!name.trim() || create.isPending}>
          Créer la catégorie
        </button>
      </form>
      {create.isError && (
        <span className="field__error" role="alert">
          {create.error.message}
        </span>
      )}
      <div className="actions">
        <button type="button" className="button button--quiet" onClick={onClose}>
          Fermer
        </button>
      </div>
    </Dialog>
  );
}

function CategoryLine({ id, name, count }: { id: number; name: string; count: number }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [value, setValue] = useState(name);
  const rename = useRenameCategory();
  const remove = useDeleteCategory();
  const toast = useToast();

  if (editing) {
    return (
      <li className="category-list__item">
        <form
          className="category-picker__new"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate(
              { id, name: value },
              {
                onSuccess: () => setEditing(false),
                onError: (error) => toast(error.message, "error"),
              },
            );
          }}
        >
          <label className="visually-hidden" htmlFor={`rename-${id}`}>
            Nouveau nom de {name}
          </label>
          <input
            id={`rename-${id}`}
            className="input"
            maxLength={80}
            value={value}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" className="button button--small" disabled={rename.isPending}>
            Renommer
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setEditing(false)}>
            Annuler
          </button>
        </form>
      </li>
    );
  }

  return (
    <li className="category-list__item">
      <span>
        <strong>{name}</strong>{" "}
        <span className="field__hint">
          {count} {count > 1 ? "entreprises" : "entreprise"}
        </span>
      </span>
      {confirming ? (
        <span className="actions">
          <span className="field__hint">Supprimer la catégorie ? Les entreprises restent suivies.</span>
          <button
            type="button"
            className="button button--danger button--small"
            disabled={remove.isPending}
            onClick={() => remove.mutate(id, { onSuccess: () => toast(`Catégorie « ${name} » supprimée.`) })}
          >
            Supprimer
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setConfirming(false)}>
            Garder
          </button>
        </span>
      ) : (
        <span className="actions">
          <button
            type="button"
            className="button button--link"
            onClick={() => {
              setValue(name);
              setEditing(true);
            }}
          >
            Renommer
          </button>
          <button type="button" className="button button--link" onClick={() => setConfirming(true)}>
            Supprimer
          </button>
        </span>
      )}
    </li>
  );
}

export function BulkCategoriesDialog({
  mode,
  count,
  onClose,
  onApply,
  pending,
}: {
  mode: "add" | "remove" | null;
  count: number;
  onClose: () => void;
  onApply: (ids: number[]) => void;
  pending: boolean;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  useEffect(() => {
    if (mode) setSelected([]);
  }, [mode]);
  const plural = count > 1 ? `${count} entreprises` : "l'entreprise";

  return (
    <Dialog
      open={mode !== null}
      onClose={onClose}
      title={mode === "remove" ? `Retirer ${plural} de catégories` : `Ranger ${plural} dans des catégories`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply(selected);
        }}
      >
        <CategoryPicker
          selected={selected}
          onChange={setSelected}
          legend={mode === "remove" ? "Catégories à retirer" : "Catégories à ajouter"}
          allowCreate={mode !== "remove"}
        />
        <div className="actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="button" disabled={!selected.length || pending}>
            {mode === "remove" ? "Retirer des catégories" : "Ajouter aux catégories"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
